import React, { useContext, useEffect, useRef, useState } from 'react';
import { DashboardBlock, DashboardCompanyInfo } from 'components/Dashboard';
import { useTranslation } from 'react-i18next';
import { COMPANIES_API } from 'api';
import { AuthContext } from 'context/auth';
import ToastifyHandler from 'utils/ToastifyHandler';
import {
	CONFIGURATIONS_COLLECTION_NAME,
	CONFIGURATION_TYPE,
	CONFIGURATION_VISIBILITY,
	USER_ROLES,
} from 'utils/constant';

import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firebaseDB } from 'utils/firebase';
import { useParams } from 'react-router-dom';
import { AddNewType } from 'components/AddNewType';
import GlassConfigUtil from 'components/GlassConfig/GlassConfigUtil';
import { AppDataContext } from 'context/appData';
import { GlassStructureContext } from 'context/configuration';
import { AccountPage } from 'pages/AccountPage';

const CustomerDashboardPage = () => {
	const { t } = useTranslation(['commonAppValues', 'firebaseErrorMessages']);
	const { configuratorData, isConfiguratorLoaded } = useContext(AppDataContext);
	const { currentUserAdditionalData, currentUserRole } = useContext(AuthContext);
	const { uiHandlerRef } = useContext(GlassStructureContext);
	const notificationsHandler = useRef(new ToastifyHandler());

	const [companyData, setCompanyData] = useState(null);

	const [draftsConfigurations, setDraftsConfigurations] = useState(null);
	const [requestsConfigurations, setRequestsConfigurations] = useState(null);
	const [ordersConfigurations, setOrdersConfigurations] = useState(null);

	const [numberOfConfigurationsByType, setNumberOfConfigurations] = useState({
		orders: 0,
		requests: 0,
		drafts: 0,
	});
	const { companyId } = useParams();

	const subscribeToListenConfigurationsChanges = useRef(null);

	const importConfigurations = configs => {
		// Replace price on drafts with current price
		configs.map(companyConfiguration =>
			GlassConfigUtil.importConfiguration(companyConfiguration, {
				uiHandlerRef,
				configuratorData,
				currentUserAdditionalData,
				updatePrices: companyConfiguration.type === CONFIGURATION_TYPE.drafts,
			})
		);
	};

	const listenCompanyConfigurationsChanges = () => {
		const configurationsCollectionRef = collection(firebaseDB, CONFIGURATIONS_COLLECTION_NAME);

		let companyConfigurationsQuery;

		if (currentUserRole === USER_ROLES.admin && companyId) {
			companyConfigurationsQuery = query(
				configurationsCollectionRef,
				where('companyId', '==', companyId)
			);
		} else {
			companyConfigurationsQuery = query(
				configurationsCollectionRef,
				where('companyId', '==', currentUserAdditionalData.companyId)
			);
		}

		subscribeToListenConfigurationsChanges.current = onSnapshot(
			companyConfigurationsQuery,
			querySnapshot => {
				const allConfigurations = [];

				querySnapshot.forEach(doc => {
					allConfigurations.push(doc.data());
				});

				importConfigurations(allConfigurations);
				const companyDraftsConfigurations = [];
				const companyRequestsConfigurations = [];
				const companyOrdersConfigurations = [];

				allConfigurations.forEach(companyConfiguration => {
					if (companyConfiguration.userVisibility === CONFIGURATION_VISIBILITY.hidden) return;
					switch (companyConfiguration.type) {
						case CONFIGURATION_TYPE.request:
							companyRequestsConfigurations.push(companyConfiguration);
							break;

						case CONFIGURATION_TYPE.order:
							companyOrdersConfigurations.push(companyConfiguration);
							break;

						case CONFIGURATION_TYPE.drafts:
						default:
							companyDraftsConfigurations.push(companyConfiguration);
							break;
					}
				});

				setRequestsConfigurations(companyRequestsConfigurations);
				setOrdersConfigurations(companyOrdersConfigurations);
				setDraftsConfigurations(companyDraftsConfigurations);
				setNumberOfConfigurations({
					requests: companyRequestsConfigurations.length,
					orders: companyOrdersConfigurations.length,
					drafts: companyDraftsConfigurations.length,
				});
			},
			error => {
				notificationsHandler.current.rejected(error.message);
			}
		);
	};

	const getCompanyData = async () => {
		if (currentUserRole !== USER_ROLES.admin || !companyId) {
			setCompanyData(currentUserAdditionalData.company);
			return;
		}

		try {
			notificationsHandler.current.pending(t('notifications:loadingCompanyData'));
			const companyRes = await COMPANIES_API.getCompany(companyId);

			if (companyRes) {
				setCompanyData(companyRes);
				notificationsHandler.current.success(t('notifications:dataLoadedSuccessfully'));
			} else {
				notificationsHandler.current.rejected(t('notifications:companyNotFound'));
			}
		} catch (error) {
			const { code } = error;
			notificationsHandler.current.rejected(t(code, { ns: 'firebaseErrorMessages' }));
		}
	};

	useEffect(() => {
		// update counts if a deletion happens in a child
		if (!requestsConfigurations || !ordersConfigurations || !draftsConfigurations) return;
		if (
			requestsConfigurations.length !== numberOfConfigurationsByType.requests ||
			ordersConfigurations.length !== numberOfConfigurationsByType.orders ||
			draftsConfigurations.length !== numberOfConfigurationsByType.drafts
		) {
			setNumberOfConfigurations({
				requests: requestsConfigurations.length,
				orders: ordersConfigurations.length,
				drafts: draftsConfigurations.length,
			});
		}
	}, [
		requestsConfigurations,
		ordersConfigurations,
		draftsConfigurations,
		numberOfConfigurationsByType,
	]);

	useEffect(() => {
		if (isConfiguratorLoaded && currentUserAdditionalData) {
			getCompanyData();
			listenCompanyConfigurationsChanges();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isConfiguratorLoaded, currentUserAdditionalData]);

	useEffect(() => {
		return () => {
			if (subscribeToListenConfigurationsChanges.current) {
				subscribeToListenConfigurationsChanges.current();
			}
		};
	}, []);

	if (!companyData) return null;
	return (
		<>
			<AccountPage adminCompanyData={companyData} />
			<section className="section dashboard">
				<div className="section__in">
					<ul className="dashboard__list">
						<li className="dashboard__item">
							<DashboardCompanyInfo
								companyName={companyData.name}
								numberOfConfigurationsByType={numberOfConfigurationsByType}
							/>
						</li>
						<li className="dashboard__item">
							{!draftsConfigurations && !requestsConfigurations && !ordersConfigurations ? (
								<AddNewType isDashboardPage />
							) : null}
						</li>
						{draftsConfigurations && draftsConfigurations.length ? (
							<li className="dashboard__item">
								<DashboardBlock
									type="drafts"
									titleLabel={t('drafts')}
									configurationData={draftsConfigurations}
									setConfigurationData={setDraftsConfigurations}
								/>
							</li>
						) : null}
						{requestsConfigurations && requestsConfigurations.length ? (
							<li className="dashboard__item">
								<DashboardBlock
									type="requests"
									titleLabel={t('requests')}
									configurationData={requestsConfigurations}
									setConfigurationData={setRequestsConfigurations}
								/>
							</li>
						) : null}
						{ordersConfigurations && ordersConfigurations.length ? (
							<li className="dashboard__item">
								<DashboardBlock
									type="orders"
									titleLabel={t('orders')}
									configurationData={ordersConfigurations}
									setConfigurationData={setOrdersConfigurations}
								/>
							</li>
						) : null}
					</ul>
				</div>
			</section>
		</>
	);
};

export default CustomerDashboardPage;
